/**
 * Rule-based "Areas to Improve" and "Recommended Next Steps" from tutor + writing analytics.
 * No AI; derived from scores and counts only.
 */

const THRESHOLD_LOW = 60;
const THRESHOLD_HIGH_SUGGESTIONS = 5;

export interface InsightInputs {
  /** Speaking category averages (0–100) */
  speakingAverages?: {
    pronunciation?: number;
    fluency?: number;
    grammar?: number;
    vocabulary?: number;
    overall?: number;
  };
  /** Writing rubric averages (0–100) */
  writingAverages?: {
    grammar?: number;
    clarity?: number;
    vocabulary?: number;
    overall?: number;
  };
  /** Total suggestion count across writing checks */
  totalWritingSuggestions?: number;
  /** Number of writing checks */
  writingChecksCount?: number;
  /** Number of tutor sessions */
  sessionsCount?: number;
}

export interface InsightResult {
  areasToImprove: string[];
  recommendedNextSteps: string[];
}

export function getAnalyticsInsights(inputs: InsightInputs): InsightResult {
  const areasToImprove: string[] = [];
  const recommendedNextSteps: string[] = [];

  const speaking = inputs.speakingAverages ?? {};
  const writing = inputs.writingAverages ?? {};
  const totalSuggestions = inputs.totalWritingSuggestions ?? 0;
  const writingCount = inputs.writingChecksCount ?? 0;
  const avgSuggestionsPerCheck = writingCount > 0 ? totalSuggestions / writingCount : 0;

  // Grammar (speaking or writing)
  const speakingGrammar = speaking.grammar ?? 0;
  const writingGrammar = writing.grammar ?? 0;
  const grammarLow = speakingGrammar > 0 && speakingGrammar < THRESHOLD_LOW || writingGrammar > 0 && writingGrammar < THRESHOLD_LOW;
  if (grammarLow) {
    areasToImprove.push('Grammar');
    recommendedNextSteps.push('Focus on grammar practice in both speaking and writing.');
  }

  // Vocabulary (speaking or writing)
  const speakingVocab = speaking.vocabulary ?? 0;
  const writingVocab = writing.vocabulary ?? 0;
  const vocabLow = speakingVocab > 0 && speakingVocab < THRESHOLD_LOW || writingVocab > 0 && writingVocab < THRESHOLD_LOW;
  if (vocabLow) {
    areasToImprove.push('Vocabulary');
    recommendedNextSteps.push('Build vocabulary through reading and practice.');
  }

  // High suggestion count → writing revision
  if (avgSuggestionsPerCheck >= THRESHOLD_HIGH_SUGGESTIONS && writingCount > 0) {
    areasToImprove.push('Writing revision');
    recommendedNextSteps.push('Practice writing revision and proofreading before submitting.');
  }

  // Fluency / pronunciation (speaking)
  const fluency = speaking.fluency ?? 0;
  const pronunciation = speaking.pronunciation ?? 0;
  const speakingLow = (fluency > 0 && fluency < THRESHOLD_LOW) || (pronunciation > 0 && pronunciation < THRESHOLD_LOW);
  if (speakingLow) {
    areasToImprove.push('Speaking (fluency / pronunciation)');
    recommendedNextSteps.push('Spend more time on speaking practice with the Tamil tutor.');
  }

  // Clarity (writing only)
  const clarity = writing.clarity ?? 0;
  if (clarity > 0 && clarity < THRESHOLD_LOW) {
    areasToImprove.push('Writing clarity');
    recommendedNextSteps.push('Focus on clear sentence structure and organization in writing.');
  }

  // Dedupe and ensure we have at least one generic step if nothing matched
  const uniqueAreas = [...new Set(areasToImprove)];
  const uniqueSteps = [...new Set(recommendedNextSteps)];
  if (uniqueSteps.length === 0) {
    uniqueSteps.push('Keep practicing regularly to maintain and improve your skills.');
  }

  return {
    areasToImprove: uniqueAreas,
    recommendedNextSteps: uniqueSteps,
  };
}
